<script setup lang="ts">
  // Framework
  import { toArray } from '@vuetify/v0'

  import { useIconContext } from '@/plugins/icons'

  // Utilities
  import { computed } from 'vue'

  export interface AppIconProps {
    icon: string
    size?: string | number
  }

  const {
    size = 18,
    ...props
  } = defineProps<AppIconProps>()

  const icons = useIconContext()

  // Cache resolved icons by name to avoid re-resolving on every render.
  // Tuple: [path, opacity, fill?] — fill overrides currentColor when provided.
  const iconCache = new Map<string, [string, number, string | undefined][]>()

  const icon = computed(() => {
    const name = props.icon
    if (iconCache.has(name)) return iconCache.get(name)!

    const array: [string, number, string | undefined][] = []
    for (const i of toArray(icons.resolve(name))) {
      const [path, opacity = 1, fill] = toArray(i)
      array.push([path as string, opacity as number, fill as string | undefined])
    }

    iconCache.set(name, array)
    return array
  })
</script>

<template>
  <i class="inline-flex align-center justify-center opacity-70">
    <svg
      aria-hidden="true"
      class="inline-block align-middle"
      fill="none"
      :height="size"
      viewBox="0 0 24 24"
      :width="size"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        v-for="([d, opacity, fill], i) in icon"
        :key="i"
        :d
        :fill="fill ?? 'currentColor'"
        :opacity
        stroke="none"
        stroke-width="0"
      />
    </svg>
  </i>
</template>
